"use client";

import { useState } from "react";
import { ChevronDown, X, Filter, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface FilterOption {
  id: string | number;
  label: string;
  count?: number;
}

interface BookingFiltersProps {
  // Search functionality
  searchQuery?: string;
  onSearchChange?: (query: string) => void;

  // Filter options
  statusOptions: FilterOption[];
  studioOptions: FilterOption[];
  hostOptions: FilterOption[];
  paymentStatusOptions: FilterOption[];
  showOptions: FilterOption[];

  // Current filter values
  filterStatus: (string | number)[];
  filterStudio: (string | number)[];
  filterHost: (string | number)[];
  filterPaymentStatus: (string | number)[];
  filterShow: (string | number)[];

  // Filter change handlers
  onFilterStatusChange: (value: string | number) => void;
  onFilterStudioChange: (value: string | number) => void;
  onFilterHostChange: (value: string | number) => void;
  onFilterPaymentStatusChange: (value: string | number) => void;
  onFilterShowChange: (value: string | number) => void;
  onClearFilters: () => void;
}

export default function BookingFilters({
  searchQuery = "",
  onSearchChange,
  statusOptions,
  studioOptions,
  hostOptions,
  paymentStatusOptions,
  showOptions,
  filterStatus,
  filterStudio,
  filterHost,
  filterPaymentStatus,
  filterShow,
  onFilterStatusChange,
  onFilterStudioChange,
  onFilterHostChange,
  onFilterPaymentStatusChange,
  onFilterShowChange,
  onClearFilters,
}: BookingFiltersProps) {
  const [openFilter, setOpenFilter] = useState<string | null>(null);

  const hasActiveFilters =
    filterStatus.length > 0 ||
    filterStudio.length > 0 ||
    filterHost.length > 0 ||
    filterPaymentStatus.length > 0 ||
    filterShow.length > 0;

  const handleCollapsibleToggle = (filterName: string) => {
    setOpenFilter(openFilter === filterName ? null : filterName);
  };

  const FilterDropdown = ({
    title,
    filterValues,
    options,
    onFilterChange,
    filterKey,
  }: {
    title: string;
    filterValues: (string | number)[];
    options: FilterOption[];
    onFilterChange: (value: string | number) => void;
    filterKey: string;
  }) => (
    <Collapsible
      open={openFilter === filterKey}
      onOpenChange={() => handleCollapsibleToggle(filterKey)}
    >
      <CollapsibleTrigger asChild>
        <Button variant="outline" className="h-9 w-[150px] justify-between">
          <span className="flex items-center">
            {title}
            {filterValues.length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                {filterValues.length}
              </Badge>
            )}
          </span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="absolute z-50 mt-1 w-56 bg-white border rounded-md shadow-lg">
        <div className="p-2 max-h-64 overflow-y-auto">
          {options.map((option) => (
            <label
              key={option.id}
              className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
            >
              <Checkbox
                checked={filterValues.includes(option.id)}
                onCheckedChange={() => onFilterChange(option.id)}
              />
              <span className="text-sm flex-1">{option.label}</span>
              {option.count !== undefined && (
                <span className="text-xs text-muted-foreground">
                  ({option.count})
                </span>
              )}
            </label>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );


  return (
    <div className="flex flex-wrap gap-3 items-center">
        <Filter className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">Filters:</span>

        <FilterDropdown
        title="Status"
        filterValues={filterStatus}
        options={statusOptions}
        onFilterChange={onFilterStatusChange}
        filterKey="status"
      />

      <FilterDropdown
        title="Studio"
        filterValues={filterStudio}
        options={studioOptions}
        onFilterChange={onFilterStudioChange}
        filterKey="studio"
      />

      <FilterDropdown
        title="Host"
        filterValues={filterHost}
        options={hostOptions}
        onFilterChange={onFilterHostChange}
        filterKey="host"
      />

      <FilterDropdown
        title="Payment Status"
        filterValues={filterPaymentStatus}
        options={paymentStatusOptions}
        onFilterChange={onFilterPaymentStatusChange}
        filterKey="paymentStatus"
      />

      <FilterDropdown
        title="Show"
        filterValues={filterShow}
        options={showOptions}
        onFilterChange={onFilterShowChange}
        filterKey="show"
      />

        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="ml-auto"
          >
            <X className="h-4 w-4 mr-1" />
            Clear all
          </Button>
        )}
    </div>
  );
}
